import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Globe,
  Plus,
  X,
  Save,
  Link2,
  ArrowRightLeft,
  Gift,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Month picker component
const MonthPicker = ({ value, onChange }) => {
  const [year, setYear] = useState(value ? value.split('-')[0] : new Date().getFullYear().toString());
  const [month, setMonth] = useState(value ? value.split('-')[1] : '');

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => (currentYear - 10 + i).toString());

  const handleChange = (newYear, newMonth) => {
    if (newYear && newMonth) {
      onChange(`${newYear}-${newMonth}`);
    }
  };

  return (
    <div className="flex gap-2">
      <select
        value={month}
        onChange={(e) => {
          setMonth(e.target.value);
          handleChange(year, e.target.value);
        }}
        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
        data-testid="month-select"
      >
        <option value="">Month</option>
        {months.map(m => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => {
          setYear(e.target.value);
          handleChange(e.target.value, month);
        }}
        className="flex h-10 w-32 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
        data-testid="year-select"
      >
        {years.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} ${year}`;
};

const Connections = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('made');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchMade, setSearchMade] = useState('');
  const [searchGot, setSearchGot] = useState('');
  
  const [connectionsMade, setConnectionsMade] = useState([]);
  const [connectionsGot, setConnectionsGot] = useState([]);

  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/connections`, getAuthHeader());
      setConnectionsMade(response.data.connections_made || []);
      setConnectionsGot(response.data.connections_got || []);
    } catch (error) {
      console.error('Error fetching connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Filter connections based on search
  const filteredConnectionsMade = connectionsMade.filter(conn => {
    if (!searchMade) return true;
    const search = searchMade.toLowerCase();
    return (
      conn.person_a?.toLowerCase().includes(search) ||
      conn.person_b?.toLowerCase().includes(search) ||
      conn.city_a?.toLowerCase().includes(search) ||
      conn.city_b?.toLowerCase().includes(search) ||
      conn.company_a?.toLowerCase().includes(search) ||
      conn.company_b?.toLowerCase().includes(search)
    );
  });

  const filteredConnectionsGot = connectionsGot.filter(conn => {
    if (!searchGot) return true;
    const search = searchGot.toLowerCase();
    return (
      conn.person?.toLowerCase().includes(search) ||
      conn.city?.toLowerCase().includes(search) ||
      conn.company?.toLowerCase().includes(search) ||
      conn.connection_made_by?.toLowerCase().includes(search) ||
      conn.connection_made_by_company?.toLowerCase().includes(search)
    );
  });

  // Connections Made handlers
  const addConnectionMade = () => {
    setConnectionsMade([...connectionsMade, {
      id: `temp-${Date.now()}`,
      person_a: '',
      city_a: '',
      company_a: '',
      person_b: '',
      city_b: '',
      company_b: '',
      month_of_connection: ''
    }]);
  };

  const removeConnectionMade = (index) => {
    setConnectionsMade(connectionsMade.filter((_, i) => i !== index));
  };

  const updateConnectionMade = (index, field, value) => {
    const updated = [...connectionsMade];
    updated[index] = { ...updated[index], [field]: value };
    setConnectionsMade(updated);
  };

  const saveConnectionsMade = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/connections/made`,
        { connections_made: connectionsMade },
        getAuthHeader()
      );
      toast.success('Connections saved');
    } catch (error) {
      console.error('Error saving connections:', error);
      toast.error('Failed to save connections');
    } finally {
      setSaving(false);
    }
  };

  // Connections Got handlers
  const addConnectionGot = () => {
    setConnectionsGot([...connectionsGot, {
      id: `temp-${Date.now()}`,
      person: '',
      city: '',
      company: '',
      connection_made_by: '',
      connection_made_by_company: '',
      month_of_connection: '',
      auto_created: false
    }]);
  };

  const removeConnectionGot = (index) => {
    setConnectionsGot(connectionsGot.filter((_, i) => i !== index));
  };

  const updateConnectionGot = (index, field, value) => {
    const updated = [...connectionsGot];
    updated[index] = { ...updated[index], [field]: value };
    setConnectionsGot(updated);
  };

  const saveConnectionsGot = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/connections/got`,
        { connections_got: connectionsGot },
        getAuthHeader()
      );
      toast.success('Connections saved');
    } catch (error) {
      console.error('Error saving connections:', error);
      toast.error('Failed to save connections');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0F172A] text-white py-4 sm:py-6 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Contacts
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-semibold">Connections</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto py-4 sm:py-8 px-4 sm:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="made" className="gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="tab-connections-made">
                <ArrowRightLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Connections</span> Made
              </TabsTrigger>
              <TabsTrigger value="got" className="gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="tab-connections-got">
                <Gift className="w-4 h-4" />
                <span className="hidden sm:inline">Connections</span> Got
              </TabsTrigger>
            </TabsList>

            {/* Connections Made Tab */}
            <TabsContent value="made" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading text-[#0F172A] flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <ArrowRightLeft className="w-5 h-5 text-[#D4AF37]" />
                      Connections You've Made
                    </span>
                    <span className="text-sm font-normal text-slate-500">
                      Track introductions you've facilitated between people in your network
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Search Bar */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchMade}
                      onChange={(e) => setSearchMade(e.target.value)}
                      placeholder="Search by name, city, or company..."
                      className="pl-10 h-10"
                      data-testid="search-connections-made"
                    />
                  </div>
                  <div className="overflow-x-auto">
                    {filteredConnectionsMade.length > 0 && (
                      <div className="min-w-[900px]">
                        {/* Header */}
                        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_150px_40px] gap-2 text-sm font-medium text-slate-500 px-1 pb-2 border-b border-slate-200">
                          <span>Person A</span>
                          <span>City</span>
                          <span>Company</span>
                          <span>Person B</span>
                          <span>City</span>
                          <span>Company</span>
                          <span>Month</span>
                          <span></span>
                        </div>
                        
                        {/* Rows */}
                        {filteredConnectionsMade.map((conn, filteredIndex) => {
                          const origIndex = connectionsMade.indexOf(conn);
                          return (
                          <div 
                            key={conn.id || filteredIndex} 
                            className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_150px_40px] gap-2 py-2 border-b border-slate-100 items-center"
                          >
                            <Input
                              value={conn.person_a}
                              onChange={(e) => updateConnectionMade(origIndex, 'person_a', e.target.value)}
                              placeholder="Name"
                              className="h-9"
                              data-testid={`made-person-a-${filteredIndex}`}
                            />
                            <Input
                              value={conn.city_a}
                              onChange={(e) => updateConnectionMade(origIndex, 'city_a', e.target.value)}
                              placeholder="City"
                              className="h-9"
                              data-testid={`made-city-a-${filteredIndex}`}
                            />
                            <Input
                              value={conn.company_a}
                              onChange={(e) => updateConnectionMade(origIndex, 'company_a', e.target.value)}
                              placeholder="Company"
                              className="h-9"
                              data-testid={`made-company-a-${filteredIndex}`}
                            />
                            <Input
                              value={conn.person_b}
                              onChange={(e) => updateConnectionMade(origIndex, 'person_b', e.target.value)}
                              placeholder="Name"
                              className="h-9"
                              data-testid={`made-person-b-${filteredIndex}`}
                            />
                            <Input
                              value={conn.city_b}
                              onChange={(e) => updateConnectionMade(origIndex, 'city_b', e.target.value)}
                              placeholder="City"
                              className="h-9"
                              data-testid={`made-city-b-${filteredIndex}`}
                            />
                            <Input
                              value={conn.company_b}
                              onChange={(e) => updateConnectionMade(origIndex, 'company_b', e.target.value)}
                              placeholder="Company"
                              className="h-9"
                              data-testid={`made-company-b-${filteredIndex}`}
                            />
                            <MonthPicker
                              value={conn.month_of_connection}
                              onChange={(value) => updateConnectionMade(origIndex, 'month_of_connection', value)}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeConnectionMade(origIndex)}
                              className="text-slate-400 hover:text-red-500 h-9 w-9"
                              data-testid={`remove-made-${filteredIndex}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {filteredConnectionsMade.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <ArrowRightLeft className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p>{searchMade ? 'No matching connections found' : 'No connections made yet'}</p>
                        <p className="text-sm text-slate-400 mt-1">{searchMade ? 'Try a different search term' : 'Track introductions you facilitate between people'}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addConnectionMade}
                      className="gap-2 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                      data-testid="add-connection-made"
                    >
                      <Plus className="w-4 h-4" />
                      Add Row
                    </Button>
                    <Button
                      onClick={saveConnectionsMade}
                      disabled={saving}
                      className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white"
                      data-testid="save-connections-made"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Connections Got Tab */}
            <TabsContent value="got" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading text-[#0F172A] flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-[#D4AF37]" />
                      Connections You've Received
                    </span>
                    <span className="text-sm font-normal text-slate-500">
                      Track introductions others have made for you
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Search Bar */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchGot}
                      onChange={(e) => setSearchGot(e.target.value)}
                      placeholder="Search by name, city, company, or introducer..."
                      className="pl-10 h-10"
                      data-testid="search-connections-got"
                    />
                  </div>
                  <div className="overflow-x-auto">
                    {filteredConnectionsGot.length > 0 && (
                      <div className="min-w-[850px]">
                        {/* Header */}
                        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_150px_40px] gap-2 text-sm font-medium text-slate-500 px-1 pb-2 border-b border-slate-200">
                          <span>Person</span>
                          <span>City</span>
                          <span>Company</span>
                          <span>Connection Made By</span>
                          <span>Company</span>
                          <span>Month</span>
                          <span></span>
                        </div>
                        
                        {/* Rows */}
                        {filteredConnectionsGot.map((conn, filteredIndex) => {
                          const origIndex = connectionsGot.indexOf(conn);
                          return (
                          <div 
                            key={conn.id || filteredIndex} 
                            className={`grid grid-cols-[1fr_1fr_1fr_1fr_1fr_150px_40px] gap-2 py-2 border-b border-slate-100 items-center ${
                              conn.auto_created ? 'bg-[#D4AF37]/5' : ''
                            }`}
                          >
                            <div className="relative">
                              <Input
                                value={conn.person}
                                onChange={(e) => updateConnectionGot(origIndex, 'person', e.target.value)}
                                placeholder="Name"
                                className="h-9"
                                data-testid={`got-person-${filteredIndex}`}
                              />
                              {conn.auto_created && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#D4AF37] rounded-full" title="Auto-created from contact"></span>
                              )}
                            </div>
                            <Input
                              value={conn.city}
                              onChange={(e) => updateConnectionGot(origIndex, 'city', e.target.value)}
                              placeholder="City"
                              className="h-9"
                              data-testid={`got-city-${filteredIndex}`}
                            />
                            <Input
                              value={conn.company}
                              onChange={(e) => updateConnectionGot(origIndex, 'company', e.target.value)}
                              placeholder="Company"
                              className="h-9"
                              data-testid={`got-company-${filteredIndex}`}
                            />
                            <Input
                              value={conn.connection_made_by}
                              onChange={(e) => updateConnectionGot(origIndex, 'connection_made_by', e.target.value)}
                              placeholder="Who introduced you?"
                              className="h-9"
                              data-testid={`got-made-by-${filteredIndex}`}
                            />
                            <Input
                              value={conn.connection_made_by_company}
                              onChange={(e) => updateConnectionGot(origIndex, 'connection_made_by_company', e.target.value)}
                              placeholder="Their company"
                              className="h-9"
                              data-testid={`got-made-by-company-${filteredIndex}`}
                            />
                            <MonthPicker
                              value={conn.month_of_connection}
                              onChange={(value) => updateConnectionGot(origIndex, 'month_of_connection', value)}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeConnectionGot(origIndex)}
                              className="text-slate-400 hover:text-red-500 h-9 w-9"
                              data-testid={`remove-got-${filteredIndex}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {filteredConnectionsGot.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p>{searchGot ? 'No matching connections found' : 'No connections received yet'}</p>
                        <p className="text-sm text-slate-400 mt-1">{searchGot ? 'Try a different search term' : 'Entries are auto-created when you add contacts with "Connected by someone"'}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addConnectionGot}
                      className="gap-2 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                      data-testid="add-connection-got"
                    >
                      <Plus className="w-4 h-4" />
                      Add Row
                    </Button>
                    <Button
                      onClick={saveConnectionsGot}
                      disabled={saving}
                      className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white"
                      data-testid="save-connections-got"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default Connections;
